import { toast } from 'react-toastify';
import type { OpenQuizAnswerBody, SelectQuizAnswerBody } from '@myme/api';
import type { Blob } from '@myme/utils';
import i18n from 'i18next';
import { ToastType } from 'src/utils';
import type { QuizActions } from './types';
import { useUpdateBlobProgress } from './useUpdateBlobProgress';

export const useBlobDetailsMutations = (
  currentBlob: Blob | null,
  mymeId: string | undefined,
  blob: Blob | undefined,
  currentBlobProgressIsFinished: boolean | undefined,
  currentModuleId: string,
  goToNextBlob: (selectedBlob?: Blob | undefined, selectedModuleId?: string | undefined) => Promise<void>
) => {
  const updateNonInteractiveBlobProgress = useUpdateBlobProgress();
  const hasNextBlob = currentBlob?.id && mymeId && currentModuleId && blob && !currentBlobProgressIsFinished;

  const updateNonInteractiveBlobProgressMutation = async () =>
    hasNextBlob &&
    updateNonInteractiveBlobProgress.mutate(
      { mymeId, currentBlobId: currentBlob.id, currentModuleId },
      {
        onSuccess: () => {
          goToNextBlob();
        }
      }
    );

  const updateSelectQuizBlobProgressMutation = async (
    questionId: SelectQuizAnswerBody['questionId'],
    answers: SelectQuizAnswerBody['answerIds'],
    isFinished: SelectQuizAnswerBody['isFinished'],
    quizActions?: QuizActions
  ) => {
    return (
      hasNextBlob &&
      updateNonInteractiveBlobProgress.mutate(
        {
          mymeId,
          currentBlobId: currentBlob.id,
          currentModuleId,
          progress: { questionId, answerIds: answers, isFinished }
        },
        {
          onSuccess: ({ data }) => {
            if (quizActions) {
              const { quizType, openGoodAnswerDialog, openBadAnswerDialog } = quizActions;

              if (quizType === 'MULTIPLE_CHOICE') {
                if (data === undefined) {
                  toast(ToastType.ERROR, i18n.t('defaults.error_message'));
                  return;
                }
                if (data.isCorrect) {
                  openGoodAnswerDialog();
                  return;
                }
                openBadAnswerDialog();
              }
            }
          }
        }
      )
    );
  };

  const updateOpenQuizBlobProgressMutation = async (
    questionId: OpenQuizAnswerBody['questionId'],
    answer: OpenQuizAnswerBody['answer'],
    isFinished: OpenQuizAnswerBody['isFinished']
  ) =>
    hasNextBlob &&
    updateNonInteractiveBlobProgress.mutate({
      mymeId,
      currentBlobId: currentBlob.id,
      currentModuleId,
      progress: { questionId, answer, isFinished }
    });

  return {
    updateNonInteractiveBlobProgressMutation,
    updateSelectQuizBlobProgressMutation,
    updateOpenQuizBlobProgressMutation
  };
};
